/**
 * 分期乐官网合作金融机构公告页面 - 主逻辑文件
 * 功能：展示合作金融机构信息列表，包含机构名称、官方网站、客服热线
 */

// 公共组件 - 基础环境与工具模块
import M_ENV from '~env@1.0'                      // 环境检测模块
import M_REQUEST from '~request@1.0'              // 请求模块
import { dataReport } from '@pc/components/libs/util'  // 数据埋点上报模块

// 子组件
import TopComponent from './component/top/index.vue'
import ListComponent from './component/list/index.vue'

// 配置与工具函数
import { G_PAGE_KEY, G_FLOOR_MAP, G_FLOOR_INFO_INIT, G_POSITION_ID } from './config/index.js'
import { GetPosition } from './common/index.js'

// 全局变量 - 数据埋点实例（延迟初始化）
let G_DATA_REPORT

// PC端请求实例
const G_REQUEST = new M_REQUEST({ gatewayType: 'platform' })

export default {
    name: 'CooperationInstitution',

    /**
     * 组件注册
     */
    components: {
        TopComponent,
        ListComponent
    },

    /**
     * 响应式数据定义
     */
    data () {
        return {
            isPc: false,                             // 是否为PC端
            loading: false,                          // 加载状态
            isInit: false,                           // 数据是否初始化完成
            floorInfo: { ...G_FLOOR_INFO_INIT },     // 楼层数据

            /**
             * 图片资源配置
             * 默认图片，接口返回后会被覆盖
             */
            imgSrc: {
                bg: '',       // 页面背景图（从接口获取）
                logo: '',     // 移动端logo（从接口获取）
                pcBack: '',   // PC端背景图（从接口获取）
                pcLogo: '',   // PC端Logo（从接口获取）
                pcTitle: '',  // PC端标题图（从接口获取）
            },

            /**
             * 图片尺寸配置
             */
            imgSize: {
                logoWidth: 0,    // 移动端Logo宽度
                logoHeight: 0,   // 移动端Logo高度
                titleWidth: 0,   // PC端标题图宽度
                titleHeight: 0,  // PC端标题图高度
            },

            /**
             * 埋点配置
             */
            reportConfig: {
                pageName: '合作金融机构公告',
                eventPosNamePrefix: '分期乐.合作金融机构公告',
            }
        }
    },

    /**
     * 计算属性
     * 从楼层数据中提取所需的页面数据
     */
    computed: {
        /**
         * 金融机构列表
         * 从中间楼层的运营位中提取
         */
        institutionList () {
            let list = GetPosition(this.floorInfo.midFloor, G_POSITION_ID.mid_floor.list) || []
            let material = list[0] || {}
            return material.item_list || []
        },

        /**
         * 底部文字
         */
        bottomText () {
            let list = GetPosition(this.floorInfo.bottomFloor, G_POSITION_ID.bottom_floor.text) || []
            let material = list[0] || {}
            return material.bottom_text || '本平台将基于经营合作情况适时更新'
        }
    },

    /**
     * 生命周期 - 页面创建
     */
    mounted () {
        // 检测设备类型
        this.detectDevice()
        // 初始化数据上报
        this.initDataReport()
        // 初始化页面
        this.initPage()
    },

    /**
     * 方法定义
     */
    methods: {
        /**
         * 检测设备类型
         * 通过userAgent判断是否为PC端
         */
        detectDevice () {
            let e1 = /\bfenqile_(ios|android)_(\d+\.\d+\.\d+)\b/.test(navigator.userAgent)
            let e2 = /\b(Android|iPhone|iPad|Mobile|Mobi|IEMobile)\b/i.test(navigator.userAgent)
            this.isPc = e1 || e2 ? false : true
        },

        /**
         * 初始化数据上报
         */
        initDataReport () {
            G_DATA_REPORT = dataReport
        },

        /**
         * 初始化页面
         */
        async initPage () {
            this.loading = true

            // 查询页面数据
            await this.queryMainInfo()

            // 标记初始化完成
            this.isInit = true
            this.loading = false

            // 上报页面浏览埋点
            this.handleReport('page_view', '页面浏览')
        },

        /**
         * 查询页面主数据
         * 从接口获取金融机构列表数据
         */
        async queryMainInfo () {
            try {
                const res = await G_REQUEST.ajax({
                    url: '/route0014/app/content/queryPage.json',
                    method: 'POST',
                    data: {
                        pageKey: G_PAGE_KEY,
                        useCache: 0,
                        params: {}
                    }
                })

                const { retcode, retmsg, result_rows } = res

                // 错误处理
                if (+retcode !== 0) {
                    this.$message.error(retmsg || '获取数据失败')
                    this.handleReport('error', '页面错误')
                    return false
                }

                // 更新楼层数据
                if (result_rows?.floor_info_list?.length) {
                    result_rows.floor_info_list.forEach(item => {
                        const floor_id = item.floor_id.replace(`${G_PAGE_KEY}.`, '')
                        const key = G_FLOOR_MAP[floor_id]
                        if (key) {
                            this.floorInfo[key] = item
                        }
                    })
                }

                // 更新图片资源
                this.updateImageSrc()

                return true
            } catch (error) {
                console.error(error)
                this.$message.error('获取数据失败')
                return false
            }
        },

        /**
         * 更新图片资源
         * 根据设备类型获取不同的图片数据
         */
        updateImageSrc () {
            // 移动端数据
            let mobileList = GetPosition(this.floorInfo.topFloor, G_POSITION_ID.top_floor.backgroundImage) || []
            let mobileMaterial = mobileList[0] || {}

            if (mobileMaterial.back_image) {
                this.imgSrc.bg = mobileMaterial.back_image
            }
            if (mobileMaterial.logo_image) {
                this.imgSrc.logo = mobileMaterial.logo_image
            }
            // 移动端Logo尺寸
            if (mobileMaterial.logo_image_width) {
                this.imgSize.logoWidth = parseInt(mobileMaterial.logo_image_width)
            }
            if (mobileMaterial.logo_image_height) {
                this.imgSize.logoHeight = parseInt(mobileMaterial.logo_image_height)
            }

            // PC端数据
            let pcList = GetPosition(this.floorInfo.topFloor, G_POSITION_ID.top_floor.pcTop) || []
            let pcMaterial = pcList[0] || {}

            if (pcMaterial.back_img) {
                this.imgSrc.pcBack = pcMaterial.back_img
            }
            if (pcMaterial.logo_img) {
                this.imgSrc.pcLogo = pcMaterial.logo_img
            }
            if (pcMaterial.title_img) {
                this.imgSrc.pcTitle = pcMaterial.title_img
            }
            // PC端标题图尺寸
            if (pcMaterial.title_img_width) {
                this.imgSize.titleWidth = parseInt(pcMaterial.title_img_width)
            }
            if (pcMaterial.title_img_height) {
                this.imgSize.titleHeight = parseInt(pcMaterial.title_img_height)
            }
        },

        /**
         * 处理返回按钮点击
         */
        handleBackClick () {
            // 数据上报
            this.handleReport('click', '返回按钮点击')
        },

        /**
         * 处理Logo点击
         */
        handleLogoClick () {
            // 数据上报
            this.handleReport('click', 'Logo点击')
        },

        /**
         * 数据上报
         * @param {string} action - 埋点动作
         * @param {string} label - 埋点标签
         */
        handleReport (action, label) {
            if (G_DATA_REPORT) {
                G_DATA_REPORT({
                    eventPosName: `${this.reportConfig.eventPosNamePrefix}.${label}`,
                    hottag: action === 'page_view' ? 'PAGE_VIEW' : 'ELEMENT_CLICK',
                    eventParams: {
                        event_code: action === 'page_view' ? 'page_view' : 'element_click'
                    }
                })
            }
        }
    }
}